import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { flushSync } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { inRange, isEmpty, find, isEqual, isNil } from "lodash";
import { useSwipeable } from "react-swipeable";
import { subscribe, isSupported } from "on-screen-keyboard-detector";
import { toast } from "react-hot-toast";
import { useAuth0 } from "@auth0/auth0-react";
import MoonLoader from "react-spinners/MoonLoader";

import PropertyInfo from "@/components/common/PropertyInfo";
// PropertyInfo listing styles (Show More, layout) still live in daily-properties.scss; that page is retired.
// TODO: Move shared listing styles into PropertyInfo (or a dedicated puzzle-listing stylesheet) and drop this import.
import "@/pages/daily-properties/daily-properties.scss";
import PuzzleListingPropertyLike from "@/pages/LiveVariant/components/Puzzle/puzzle-listing-property-like";
import { LIVE_VARIANT_VERSIONS } from "@/constants/live-variant-versions";
import {
    getLiveVariantDailyProperty,
    getPropertyPuzzleStats,
    isRemovedPuzzleError,
    resolveLink,
    type PuzzleStatsResponse,
} from "@/services/PropertyService";
import { REMOVED_PUZZLE_MESSAGE } from "@/constants/playable-property-errors";
import {
    persistGuestPuzzleCompletionToPendingStore,
    readPendingGuestGuessForPlayable,
} from "@/helpers/puzzlePendingGuessesStorage";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import { getUserGuess, submitGuess, type UserGuessSubmitStreak } from "@/services/UserService";
import useGAEvent from "@/hooks/useGAEvent";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import useLocalStorage from "@/hooks/useLocalStorage";
import useModalState from "@/hooks/useModalState";
import useScrollLock from "@/hooks/useScrollLock";

import { IoCalculatorOutline, IoChevronUp } from "react-icons/io5";
import { motion } from "framer-motion";
import TileRow from "./TileRow";
import Toggle from "./Toggle";
import { formatPrice, pxToRem, validateImageURL, getDisplayPrice } from "@/helpers/helpers";
import CustomButton from "@/components/common/buttons/custom-button/custom-button";

import FullListingModal from "./FullListingModal";
import moment from "moment";
import { buildPuzzleShareDetails, buildPropertyShareLabel } from "@/helpers/puzzleShare";
import { useShareScoreSheet } from "@/components/common/share-score-sheet/share-score-sheet-context";
import {
    appendPuzzleViewToPath,
    hasExplicitPuzzleListingViewInSearch,
    isLegacyPuzzleUiHash,
    normalizeVariantLinkKey,
    parsePuzzleViewFromLocation,
    PUZZLE_VIEW_PARAM,
} from "@/helpers/puzzle-view-url";
import { buildPuzzleListingNavigationUrl, withCurrentSearchParams } from "@/helpers/withCurrentSearchParams";
import { usePuzzleViewUrl } from "@/hooks/usePuzzleViewUrl";
import {
    buildPuzzleListingPathFromNextItem,
    fetchNextPlayableProperty,
    type PuzzleGuessedPropertyResult,
} from "@/helpers/puzzleNextPlayableProperty";
import { isMobile } from "react-device-detect";
import useVisualViewport from "@/hooks/useVisualViewport";
import { useLiveVariant } from "@/context/LiveVariantContext";
import { useUser } from "@/context/UserContext";
import { logger } from "@/services/LoggerService";
import {
    PUZZLE_GUEST_STREAK_MODAL_SHOWN_KEY,
    PUZZLE_OPEN_SCOREBOARD_KEY,
    PUZZLE_REGISTER_LOCATION_KEY,
} from "@/constants/puzzle-storage.constants";
import { readPuzzleFocusCommentIdFromSearch } from "@/helpers/puzzle-comment-url";
import {
    MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL,
    MIXPANEL_REGISTER_LOCATION_MOBILE_PUZZLE_BANNER,
    MIXPANEL_REGISTER_LOCATION_SCOREBOARD_SAVE,
    MIXPANEL_REGISTER_LOCATION_STREAK_MODAL,
} from "@/constants/mixpanel.constants";
import PageHead from "@/components/common/PageHead";
import OneTapSignInBanner from "@/components/common/OneTapSignIn/OneTapSignInBanner";
import OneTapSignInModal from "@/components/common/OneTapSignIn/OneTapSignInModal";
import AdSenseAd from "@/components/common/AdSenseAd/AdSenseAd";
import config from "@/config";
import ScoreboardFeed from "./ScoreboardFeed";
import type { ScoreboardViewLocation } from "./ScoreboardFeed";
import type { GuessCell } from "./ScoreboardFeed/guess-grid";
import StreakModal from "./StreakModal";

const MAX_DIGIT = 8;
const MAX_GUESS = 6;
const INCORRECT_THRESHOLD = 2;
const INITIAL_GUESS_INPUT_HEIGHT = 6;
/** Base height (rem) for the expanded tile rows / guess grid content */
const EXPANDED_CONTENT_BASE_HEIGHT_REM = 19;
/** Extra offset (rem) for chevron + "Hide guesses" row when drawer is expanded */
const EXPANDED_TOP_OFFSET_REM = 0.5;

const GAME_STATES = Object.freeze({ ONGOING: "ONGOING", LOSE: "LOSE", WIN: "WIN" });
/** Matches Tile flip: (MAX_DIGIT - 1) * 0.5s delay + 0.5s duration + buffer. */
const TILE_ROW_FLIP_ANIMATION_MS = (MAX_DIGIT - 1) * 500 + 500 + 250;

function arePuzzleTooltipsVisible(puzzleTooltipData: unknown): boolean {
    if (isEmpty(puzzleTooltipData)) {
        return false;
    }
    const data = puzzleTooltipData as { correct?: { hidden?: boolean }; close?: { hidden?: boolean } };
    return data.correct?.hidden === false || data.close?.hidden === false;
}

function toPlayablePropertyIdKey(playablePropertyId: string | number | null | undefined): string | null {
    if (playablePropertyId == null || playablePropertyId === "") {
        return null;
    }
    return String(playablePropertyId);
}

type PuzzleGuessMutationVariables = {
    guess: number[];
    score: number;
    areaId: string;
    playablePropertyId: string;
};

function isSubmittedPuzzleStillActive(params: {
    submittedPlayablePropertyId: string | null;
    currentPlayablePropertyId: string | null;
}): boolean {
    return (
        params.submittedPlayablePropertyId != null &&
        params.submittedPlayablePropertyId === params.currentPlayablePropertyId
    );
}
/** How long the unavailable "Oops" copy shows before auto-skipping to the next puzzle. */
const UNAVAILABLE_OOPS_DISPLAY_MS = 2_500;
/** Max consecutive auto-skips when listings fail image validation (avoids infinite redirect loops). */
const MAX_UNAVAILABLE_AUTO_SKIP_ATTEMPTS = 8;

function shouldShowRegisteredStreakModal(streak: UserGuessSubmitStreak | null | undefined): boolean {
    return streak?.streakChangeType === "incremented" || streak?.streakChangeType === "reset";
}

function hasGuestStreakModalBeenShownThisSession(): boolean {
    try {
        return sessionStorage.getItem(PUZZLE_GUEST_STREAK_MODAL_SHOWN_KEY) === "true";
    } catch {
        return false;
    }
}

function markGuestStreakModalShownThisSession(): void {
    try {
        sessionStorage.setItem(PUZZLE_GUEST_STREAK_MODAL_SHOWN_KEY, "true");
    } catch {
        // ignore
    }
}

/** Delay before auto-opening the scoreboard guest auth modal after the streak modal closes (ms). */
const GUEST_SCOREBOARD_AUTH_NOT_BEFORE_AFTER_STREAK_MS = 15_000;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

type GuestPendingShortLinkSource = "api" | "pending-store" | "none";

function resolveGuestPendingShortLink(params: {
    liveVariantState: { shortLink?: string | null } | null | undefined;
    property: { shortLink?: string; short_link?: string } | null | undefined;
    playablePropertyId: string | null | undefined;
}): { shortLink: string | null; source: GuestPendingShortLinkSource } {
    const fromApi =
        params.liveVariantState?.shortLink ??
        params.property?.shortLink ??
        params.property?.short_link;
    if (fromApi != null && String(fromApi).trim()) {
        return { shortLink: String(fromApi).trim(), source: "api" };
    }
    const playableId =
        params.playablePropertyId != null ? String(params.playablePropertyId).trim() : "";
    if (playableId) {
        const existing = readPendingGuestGuessForPlayable(playableId);
        if (existing?.shortLink) {
            return { shortLink: existing.shortLink, source: "pending-store" };
        }
    }
    return { shortLink: null, source: "none" };
}

/**
 * Keyboard overlap in px (always ≥ 0). `useVisualViewport`'s `offsetHeight` is a previous−current delta;
 * when the keyboard closes the delta can go negative and shrink the drawer height.
 */
const getKeyboardObstructionPx = (): number => {
    if (typeof window === "undefined") return 0;
    const vv = window.visualViewport;
    if (!vv) return 0;
    return Math.max(0, window.innerHeight - vv.height);
};

const SEARCH_URL = "https://www.realtor.com/realestateandhomes-search";

// SEO helper: beds/baths sometimes arrive as numeric strings (e.g. "2.5").
// We must never output "undefined"/"NaN" in metadata; default missing/invalid to "0".
const formatPropertyCountDisplay = (value: unknown): string => {
    if (isNil(value)) return "0";

    const normalized = typeof value === "string" ? value.trim() : value;
    if (typeof normalized === "string" && isEmpty(normalized)) return "0";

    const n = typeof normalized === "number" ? normalized : Number(normalized as any);
    if (!Number.isFinite(n) || n < 0) return "0";

    if (Number.isInteger(n)) return String(n);
    // Trim trailing zeros from decimals: 3.50 -> 3.5
    return String(n)
        .replace(/(\.\d*?)0+$/, "$1")
        .replace(/\.$/, "");
};

interface PuzzleProps {
    version: string;
}

type Property = any;

type GuessValue = string | null;

export default function Puzzle({ version }: PuzzleProps) {
    const { sendEvent } = useGAEvent();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { openShareScoreSheet } = useShareScoreSheet();
    const { setModalId } = useModalState();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { liveVariantState, setLiveVariantState } = useLiveVariant();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const puzzleFeedSlc = searchParams.get("slc");
    const [shownHelpModals, setShownHelpModal] = useLocalStorage<string[]>("shown-help-modals", []);
    const viewport = useVisualViewport();
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [property, setProperty] = useState<Property | null>(null);
    const [guesses, setGuesses] = useState<GuessValue[]>(Array<GuessValue>(MAX_GUESS).fill(null));
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [expand, setExpand] = useState<boolean>(false);
    const [inputFocused, setInputFocused] = useState<boolean>(false);
    const [gameState, setGameState] = useState<string>(GAME_STATES.ONGOING);
    const [ongoingAnimation, setOngoingAnimation] = useState<string>("idle");
    const [guessInputHeight, setGuessInputHeight] = useState<number | undefined>();
    const [virtualKeyboardShown, setVirtualKeyboardShown] = useState<boolean>(false);
    const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showFullListingModal, setShowFullListingModal] = useState<boolean>(false);
    const [isPhotosVerified, setIsPhotosVerified] = useState<boolean>(false);
    const [isToastDisplayed, setIsToastDisplayed] = useState<boolean>(false); // Prevent multiple toast appearing
    const [showRemovedPuzzle, setShowRemovedPuzzle] = useState<boolean>(false);
    const [showUnavailablePuzzle, setShowUnavailablePuzzle] = useState<boolean>(false);
    const [isRedirectingFromUnavailablePuzzle, setIsRedirectingFromUnavailablePuzzle] = useState<boolean>(false);
    const [unavailableAutoSkipNoMorePuzzles, setUnavailableAutoSkipNoMorePuzzles] = useState<boolean>(false);
    const unavailableAutoSkipAttemptsRef = useRef<number>(0);
    const unavailableAutoSkipExcludedRef = useRef<string[]>([]);
    const [isSavingUserGuess, setIsSavingUserGuess] = useState<boolean>(false);
    const hasTrackedPuzzleScoreRef = useRef<boolean>(false);
    const [hasFeedReturnAnchor] = useState<boolean>(() => {
        try {
            // Check sessionStorage first (more reliable for immediate navigation)
            const fromFeed = sessionStorage.getItem("from-puzzle-feed");
            if (fromFeed === "true") {
                sessionStorage.removeItem("from-puzzle-feed"); // Clean up after reading
                return true;
            }
            // Fallback to localStorage check
            const stored = localStorage.getItem("puzzle-feed-state");
            if (!stored) return false;
            const parsed = JSON.parse(stored);
            // Check if we have a valid feed state (either lastScrollPosition or any state object)
            return parsed && (typeof parsed?.lastScrollPosition === "number" || Object.keys(parsed).length > 0);
        } catch {
            return false;
        }
    });
    const currentVersion = (LIVE_VARIANT_VERSIONS as any)[version];
    const { date, propertyDetails } = useParams();
    // Keep backend-compatible semantics: if hash is a legacy slug continuation, preserve it as "#..."
    // in variantLink so resolve uses the same longLink content (browser-safe path can still use %23).
    const variantLink = useMemo(() => {
        if (!date || !propertyDetails) return null;
        const hashRaw = (window.location?.hash || "").replace(/^#/, "");
        const isUiHash = isLegacyPuzzleUiHash(hashRaw);
        if (hashRaw && !isUiHash && propertyDetails.endsWith("-")) {
            return `${date}/${propertyDetails}#${hashRaw}`;
        }
        return `${date}/${propertyDetails}`;
    }, [date, propertyDetails]);
    const [puzzleShareObject, setPuzzleShareObject] = useLocalStorage<any>("puzzle_share_object", {});
    const [guessedPropertiesLocal, setGuessedProperties] = useLocalStorage<any[]>("guessed_properties", []);
    const [guessedPropertyResultsLocal, setGuessedPropertyResults] = useLocalStorage<any[]>(
        "guessed_property_results",
        [],
    );
    const [puzzleTooltipData, setPuzzleTooltipData] = useLocalStorage<any>("puzzle-tooltip-data", {});
    const [visitorStatus] = useLocalStorage<string>("visitor-status", "");
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState<boolean>(false);
    const [bannerStep, setBannerStep] = useState<1 | 2>(1);
    const [bannerActuallyVisible, setBannerActuallyVisible] = useState<boolean>(false); // Tracks if banner is still animating
    const { puzzleView, setPuzzleView } = usePuzzleViewUrl();

    const handlePuzzleTabChange = useCallback(
        (tab: "guesses" | "scoreboard") => {
            setActiveTab(tab);
            if (tab === "scoreboard") {
                scoreboardAutoOpenPendingRef.current = false;
                setScoreboardViewLocation("Puzzle Drawer");
                setPuzzleView("scoreboard", { replace: true });
            } else {
                setPuzzleView("listing", { replace: true });
            }
        },
        [setPuzzleView],
    );
    const [activeTab, setActiveTab] = useState<"guesses" | "scoreboard">(() => {
        const initialView = parsePuzzleViewFromLocation({
            search: window.location.search,
            hash: window.location.hash,
        });
        return initialView === "scoreboard" ? "scoreboard" : "guesses";
    });
    const [scoreboardViewLocation, setScoreboardViewLocation] = useState<ScoreboardViewLocation>("Puzzle Drawer");
    const [showStreakModal, setShowStreakModal] = useState<boolean>(false);
    /** Guest scoreboard auth modal must not open before this unix ms (set when streak closes). */
    const [guestScoreboardAuthNotBeforeMs, setGuestScoreboardAuthNotBeforeMs] = useState<number>(0);
    const [streakData, setStreakData] = useState<{
        currentStreak: number;
        previousStreak: number;
        streakChangeType: "incremented" | "reset" | "already_completed" | "guest_static";
    } | null>(null);
    const prevVariantLinkRef = useRef<string | null>(null);
    const hasAutoOpenedScoreboardRef = useRef<boolean>(false);
    /** Preserves post-completion scoreboard `viewLocation` when `?view=scoreboard` sync runs. */
    const scoreboardAutoOpenPendingRef = useRef<boolean>(false);
    /** Playable property id for the in-flight WIN/LOSE; cleared on puzzle navigation reset. */
    const completionPlayablePropertyIdRef = useRef<string | null>(null);
    /** Playable property id already submitted this session; cleared on puzzle navigation reset. */
    const submittedGuessPlayablePropertyIdRef = useRef<string | null>(null);
    /** Tracks playable property changes to clear stale WIN/LOSE from the previous listing. */
    const prevPlayablePropertyIdForGameRef = useRef<string | null>(null);
    /** Opens streak modal only after completion auto-redirects to the scoreboard tab. */
    const pendingShowStreakModalRef = useRef<boolean>(false);
    /** Skips one guess-hydration pass after route change so stale playablePropertyId cannot restore prior tiles. */
    const suppressGuessHydrationRef = useRef(false);
    const puzzleGuessMutationResetRef = useRef<(() => void) | null>(null);
    const setPuzzleTooltipDataRef = useRef(setPuzzleTooltipData);
    setPuzzleTooltipDataRef.current = setPuzzleTooltipData;

    const resetPuzzlePlaySession = useCallback(() => {
        hasAutoOpenedScoreboardRef.current = false;
        scoreboardAutoOpenPendingRef.current = false;
        submittedGuessPlayablePropertyIdRef.current = null;
        completionPlayablePropertyIdRef.current = null;
        pendingShowStreakModalRef.current = false;
        hasTrackedPuzzleScoreRef.current = false;
        puzzleGuessMutationResetRef.current?.();
        setGuesses(Array<GuessValue>(MAX_GUESS).fill(null));
        setCurrentIndex(0);
        setGameState(GAME_STATES.ONGOING);
        setOngoingAnimation("idle");
        setIsSubmitting(false);
        setIsToastDisplayed(false);
        setIsSavingUserGuess(false);
        setPuzzleTooltipDataRef.current({});
    }, []);

    // Reset puzzle-local UI and refs when navigating to a different daily puzzle (same mounted route).
    useEffect(() => {
        const previousVariantLink = prevVariantLinkRef.current;
        const currentVariantLink = variantLink;
        const isPuzzleRouteChange =
            previousVariantLink != null &&
            currentVariantLink != null &&
            normalizeVariantLinkKey(previousVariantLink) !== normalizeVariantLinkKey(currentVariantLink);
        prevVariantLinkRef.current = variantLink;

        if (!isPuzzleRouteChange) {
            return;
        }

        suppressGuessHydrationRef.current = true;
        resetPuzzlePlaySession();
        setStreakData(null);
        setShowStreakModal(false);
        setGuestScoreboardAuthNotBeforeMs(0);
        setIsPhotosVerified(false);
        setShowUnavailablePuzzle(false);
        setShowRemovedPuzzle(false);
        setIsRedirectingFromUnavailablePuzzle(false);
        setUnavailableAutoSkipNoMorePuzzles(false);
        setProperty(null);
        setActiveTab("guesses");
        navigate(buildPuzzleListingNavigationUrl(`/puzzle/${variantLink}`), { replace: true });
        setScoreboardViewLocation("Puzzle Drawer");
        setLiveVariantState((prev) => ({
            ...prev,
            playablePropertyId: null,
            shortLink: null,
        }));
        void queryClient.removeQueries({ queryKey: ["puzzle_daily_property_guess"] });
    }, [variantLink, navigate, queryClient, resetPuzzlePlaySession, setLiveVariantState]);

    useLayoutEffect(() => {
        if (!variantLink) {
            return;
        }
        if (hasExplicitPuzzleListingViewInSearch(location.search)) {
            return;
        }
        const hashRaw = location.hash.replace(/^#/, "").toLowerCase();
        if (hashRaw === "scoreboard") {
            return;
        }
        const params = new URLSearchParams(
            location.search.startsWith("?") ? location.search.slice(1) : location.search,
        );
        const currentView = params.get(PUZZLE_VIEW_PARAM);
        if (currentView === "vertical" || currentView === "full_screen") {
            return;
        }
        navigate(buildPuzzleListingNavigationUrl(location.pathname), { replace: true });
    }, [location.hash, location.pathname, location.search, navigate, variantLink]);

    useLayoutEffect(() => {
        const focusCommentId = readPuzzleFocusCommentIdFromSearch(location.search);
        if (!focusCommentId || puzzleView === "scoreboard") {
            return;
        }
        setPuzzleView("scoreboard", { replace: true });
    }, [location.search, puzzleView, setPuzzleView]);

    useEffect(() => {
        const hashRaw = location.hash.replace(/^#/, "").toLowerCase();
        if (hashRaw === "scoreboard" && puzzleView !== "scoreboard") {
            setPuzzleView("scoreboard", { replace: true });
            return;
        }
        try {
            if (sessionStorage.getItem(PUZZLE_OPEN_SCOREBOARD_KEY) === "true") {
                sessionStorage.removeItem(PUZZLE_OPEN_SCOREBOARD_KEY);
                if (puzzleView !== "scoreboard") {
                    setPuzzleView("scoreboard", { replace: true });
                }
            }
        } catch (error) {
            logger.warn("Failed to apply puzzle scoreboard open intent", {
                context: "Puzzle",
                error: String(error),
            });
        }
        if (puzzleView === "scoreboard") {
            setExpand(true);
            setActiveTab("scoreboard");
            if (scoreboardAutoOpenPendingRef.current) {
                scoreboardAutoOpenPendingRef.current = false;
                setScoreboardViewLocation("Auto");
            } else {
                setScoreboardViewLocation("Puzzle Drawer");
            }
            return;
        }
        if (puzzleView === "listing") {
            setActiveTab("guesses");
        }
    }, [location.hash, puzzleView, setPuzzleView]);

    // Track Mixpanel Page View once per property with a 5-minute throttle window
    const lastTrackedPropertyIdRef = useRef(null);
    useEffect(() => {
        if (!property?.id) return;
        if (lastTrackedPropertyIdRef.current === property.id) return;
        const key = `page_view_tracked:${window.location.pathname}:property:${property.id}`;
        const now = Date.now();
        const lastTsRaw = sessionStorage.getItem(key);
        const lastTs = lastTsRaw ? Number(lastTsRaw) : 0;
        const FIVE_MIN_MS = 5 * 60 * 1000;
        if (now - lastTs < FIVE_MIN_MS) return;

        sendMixpanelEvent("Page View", {
            isAuthenticated: !!userState?.id,
            visitorStatus: userState?.id ? null : visitorStatus || "new-guest",
            pageName: "Puzzle",
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.emailAddress || null,
            pageUrl: window.location.href,
        });

        lastTrackedPropertyIdRef.current = property.id;
        sessionStorage.setItem(key, String(now));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property?.id]);

    const dailyPropertyGuess = useQuery({
        queryKey: [
            "puzzle_daily_property_guess",
            property?.id,
            (userState as any)?.id,
            liveVariantState?.playablePropertyId,
            userState,
            liveVariantState,
        ],
        queryFn: () =>
            getUserGuess(
                (userState as any)?.id,
                property?.id,
                null as any,
                null as any,
                String((liveVariantState as any)?.playablePropertyId ?? ""),
                "puzzle",
            ),
        gcTime: 0,
        enabled: isAuthenticated && !isNil((userState as any)?.id) && !isNil(property?.id),
    });

    const puzzleGuessMutation = useMutation({
        mutationFn: (userGuess: PuzzleGuessMutationVariables) => {
            const resolvedShortLink = resolveGuestPendingShortLink({
                liveVariantState: liveVariantState as { shortLink?: string },
                property: property as { shortLink?: string; short_link?: string },
                playablePropertyId: userGuess.playablePropertyId,
            }).shortLink;
            return submitGuess({
                userId: (userState as any)?.id,
                propertyId: property?.id,
                guessScore: {
                    guess: userGuess.guess,
                    score: userGuess.score,
                    excludeScore: false,
                    areaId: userGuess.areaId ?? "",
                },
                shortLink: resolvedShortLink ?? undefined,
                version: "puzzle",
                isArchivePlay: Boolean(!(liveVariantState as any)?.same_with_current_date),
            });
        },
        onError: (error, variables) => {
            submittedGuessPlayablePropertyIdRef.current = null;
            logger.error("Failed to save puzzle guess for registered user", error, {
                context: "Puzzle.puzzleGuessMutation",
                userId: userState?.id,
                playablePropertyId: variables.playablePropertyId,
            });
            toast.error("Could not save your score. Please try again.");
        },
        onSuccess: async (response, variables) => {
            const submittedPlayablePropertyId = toPlayablePropertyIdKey(variables.playablePropertyId);
            const currentPlayablePropertyId = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
            const isStillOnSubmittedPuzzle = isSubmittedPuzzleStillActive({
                submittedPlayablePropertyId,
                currentPlayablePropertyId,
            });
            if (isStillOnSubmittedPuzzle) {
                const streak = response?.data?.streak;
                if (shouldShowRegisteredStreakModal(streak)) {
                    setStreakData({
                        currentStreak: streak!.currentStreak,
                        previousStreak: streak!.previousStreak,
                        streakChangeType: streak!.streakChangeType,
                    });
                    pendingShowStreakModalRef.current = true;
                }
                void dailyPropertyGuess.refetch();
            }
            if (submittedPlayablePropertyId) {
                void queryClient.invalidateQueries({
                    queryKey: ["puzzle-scoreboard-current-user", submittedPlayablePropertyId],
                });
            }
            if (userState?.id) {
                await queryClient.refetchQueries({
                    queryKey: ["puzzle-feed"],
                    exact: false,
                });
            }
        },
        onSettled: () => {
            setIsSavingUserGuess(false);
        },
    });
    puzzleGuessMutationResetRef.current = puzzleGuessMutation.reset;


    const formattedGuesses = (
        (dailyPropertyGuess?.data as any)?.guess?.map((item: any) =>
            item?.guess ? String(item.guess).padStart(8, "0") : null,
        ) || []
    )
        .concat(Array(6).fill(null))
        .slice(0, 6);

    const authenticatedGuessLength = (dailyPropertyGuess?.data as any)?.guess?.length;
    const propertyPlayDateForGuessed = (liveVariantState as any)?.property_play_date;
    const playablePropertyIdForGuessed = liveVariantState?.playablePropertyId;

    const guessedResult = useMemo(
        () =>
            isAuthenticated
                ? {
                      guesses: formattedGuesses,
                      currentIndex: authenticatedGuessLength ?? 0,
                  }
                : find(
                      guessedPropertyResultsLocal,
                      (result) =>
                          result?.playablePropertyId === playablePropertyIdForGuessed &&
                          result?.version === version &&
                          (result as any)?.property_play_date === propertyPlayDateForGuessed,
                  ),
        [
            isAuthenticated,
            formattedGuesses,
            authenticatedGuessLength,
            guessedPropertyResultsLocal,
            playablePropertyIdForGuessed,
            propertyPlayDateForGuessed,
            version,
        ],
    );

    const isPropertyGuessed = isAuthenticated
        ? Boolean(authenticatedGuessLength)
        : Boolean(
              playablePropertyIdForGuessed &&
                  guessedPropertiesLocal?.some(
                      (id) => String(id) === String(playablePropertyIdForGuessed),
                  ) &&
                  guessedResult,
          );

    const dailyProperties = useQuery({
        queryKey: ["live-variant-daily-property", variantLink, userState?.id ?? "anonymous"],
        queryFn: () => {
            return getLiveVariantDailyProperty({
                variantLink: variantLink ?? undefined,
                requestingUserId: userState?.id ? String(userState.id) : undefined,
            });
        },
        enabled: !!variantLink,
        gcTime: 0,
        retry: false, // Don't retry on error to prevent infinite loops
    });

    // If required route params (date/propertyDetails) are missing (i.e., user navigated to /:version like /puzzle), redirect to the /puzzle feed/landing page
    useEffect(() => {
        if (!variantLink) {
            // Navigate to the puzzle feed route, not the version route
            const puzzleVersion: any = (LIVE_VARIANT_VERSIONS as any).v5;
            const puzzleFeedUrl = puzzleVersion?.url || "puzzle";
            navigate(withCurrentSearchParams(`/${puzzleFeedUrl}`), { replace: true });
        }
    }, [variantLink, navigate]);

    // Handle daily properties errors
    useEffect(() => {
        if (dailyProperties.error) {
            console.error("Error loading puzzle property:", dailyProperties.error);
            logger.error("Error loading puzzle property", dailyProperties.error, {
                context: "Puzzle",
                userId: userState?.id,
            });
            setShowRemovedPuzzle(isRemovedPuzzleError(dailyProperties.error));
            setShowUnavailablePuzzle(false);
            setIsPhotosVerified(true);
        }
    }, [dailyProperties.error, userState?.id]);

    // Handle daily properties success
    useEffect(() => {
        const response = dailyProperties.data as any;
        const payload = response?.data;
        if (!payload) return;

        setShowRemovedPuzzle(false);
        setShowUnavailablePuzzle(false);
        setProperty(payload);

        if (liveVariantState?.playablePropertyId) return;
        if (!variantLink) return;

        const run = async () => {
            try {
                const longLink = `/${variantLink}`;
                const response = await resolveLink({ longLink });
                const data = (response as any)?.data;
                const playableProperty = Array.isArray(data) ? data[0] : data;
                const playablePropertyId = playableProperty?.id ?? null;
                const shortLink = playableProperty?.shortLink ?? playableProperty?.short_link ?? null;

                if (playablePropertyId) {
                    setLiveVariantState((prev) => ({
                        ...prev,
                        playablePropertyId,
                        ...(shortLink ? { shortLink } : {}),
                    }));
                }
            } catch (error) {
                logger.error("Error resolving playablePropertyId from longLink", error, {
                    context: "Puzzle",
                    variantLink,
                });
            }
        };

        run();
    }, [dailyProperties.data, liveVariantState?.playablePropertyId, setLiveVariantState, variantLink]);

    const isLoading = variantLink
        ? dailyProperties?.isLoading || dailyProperties?.isFetching || !property || !isPhotosVerified
        : false;

    // Use external_property_link if available and not empty, otherwise default to Realtor.com URL
    const externalLink = (property as any)?.external_property_link || (property as any)?.externalPropertyLink;
    const zipCode = (property as any)?.zipCode;
    const listPrice = (property as any)?.listPrice;

    const hasValidZipAndPrice = !isNil(zipCode) && !isNil(listPrice);
    const url =
        externalLink && externalLink.trim() !== ""
            ? externalLink
            : hasValidZipAndPrice
              ? `${SEARCH_URL}/${zipCode}/price-${listPrice}-${listPrice}`
              : SEARCH_URL; // Fallback to base URL if zipCode or listPrice is missing
    const actualPriceValue = useMemo(() => {
        if (!property) return undefined;
        const price = Number(getDisplayPrice(property));
        return price || undefined;
    }, [property]);
    const actualPriceSplit = useMemo(() => {
        return actualPriceValue ? actualPriceValue.toString().padStart(MAX_DIGIT, "0").split("") : [];
    }, [actualPriceValue]);

    const correctGuessIndex = guesses.findIndex((guess) => +(guess as any) === actualPriceValue);
    const validGuesses = guesses.filter((guess) => !isEmpty(guess));
    const scoreBadgeGuesses = useMemo<GuessCell[][]>(() => {
        if (!actualPriceValue || validGuesses.length === 0) return [];
        const actualDigits = String(actualPriceValue).padStart(MAX_DIGIT, "0").split("");

        return validGuesses.map((guessValue) => {
            const rowDigits = String(guessValue).padStart(MAX_DIGIT, "0").split("");
            return rowDigits.map((digit, idx) => {
                const guessNum = Number(digit);
                const actualNum = Number(actualDigits[idx]);
                const status: GuessCell["status"] =
                    guessNum === actualNum
                        ? "correct"
                        : Math.abs(guessNum - actualNum) <= INCORRECT_THRESHOLD
                          ? "close"
                          : "wrong";
                return { digit, status };
            });
        });
    }, [validGuesses, actualPriceValue]);
    const score = (correctGuessIndex !== -1 ? correctGuessIndex : (validGuesses as any)?.length) + 1;
    const isPuzzleLose = score > 6;
    const isGameCompleted = gameState !== GAME_STATES.ONGOING;
    const isUserRegistered = isAuthenticated || Boolean(userState?.is_created);

    // Persist registered guesses via API once the puzzle is complete. Separate from scoreboard UI so a
    // slow Auth0/user hydration cannot skip submitGuess when hasAutoOpenedScoreboardRef is already set.
    useEffect(() => {
        if (gameState === GAME_STATES.ONGOING || ongoingAnimation !== "idle") {
            return;
        }
        if (arePuzzleTooltipsVisible(puzzleTooltipData)) {
            return;
        }
        if (!isUserRegistered || !userState?.id) {
            return;
        }
        const currentPlayablePropertyId = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
        if (
            !currentPlayablePropertyId ||
            submittedGuessPlayablePropertyIdRef.current === currentPlayablePropertyId
        ) {
            return;
        }
        const guessNumeric = validGuesses
            .map((g) => parseInt(String(g).replace(/\D/g, ""), 10))
            .filter((n: number) => Number.isFinite(n) && n > 0);
        if (guessNumeric.length === 0) {
            return;
        }
        const resolvedShortLink = resolveGuestPendingShortLink({
            liveVariantState: liveVariantState as { shortLink?: string },
            property: property as { shortLink?: string; short_link?: string },
            playablePropertyId:
                liveVariantState?.playablePropertyId != null
                    ? String(liveVariantState.playablePropertyId)
                    : undefined,
        }).shortLink;
        if (!resolvedShortLink) {
            logger.warn("Puzzle submit skipped: missing shortLink", {
                context: "Puzzle.submitRegisteredGuess",
                playablePropertyId: liveVariantState?.playablePropertyId,
            });
            return;
        }
        submittedGuessPlayablePropertyIdRef.current = currentPlayablePropertyId;
        setIsSavingUserGuess(true);
        puzzleGuessMutation.mutate({
            guess: guessNumeric,
            score,
            areaId: (userState as any)?.currentAreaId ?? "",
            playablePropertyId: currentPlayablePropertyId,
        });
    }, [
        gameState,
        ongoingAnimation,
        puzzleTooltipData,
        isUserRegistered,
        liveVariantState,
        property,
        puzzleGuessMutation,
        score,
        userState,
        validGuesses,
    ]);

    const guestGuessesForScoreboardApi = useMemo(() => {
        if (isAuthenticated || !isPropertyGuessed) return undefined;
        const parsed = validGuesses
            .map((g) => {
                if (g === null || g === undefined || g === "") return null;
                const digits = String(g).replace(/\D/g, "");
                if (!digits) return null;
                const n = Number(digits);
                return Number.isFinite(n) && n > 0 ? n : null;
            })
            .filter((n): n is number => n !== null);
        return parsed.length > 0 ? parsed : undefined;
    }, [isAuthenticated, isPropertyGuessed, validGuesses]);

    // Compose SEO title/description from validated pieces so metadata never contains "undefined"/"NaN".
    // Beds/baths: always show a number (supports decimals like 2.5), defaulting to 0 when missing/invalid.
    const seoTitle = (() => {
        if (!property || !(property as any)?.streetAddress) {
            return "PriceMe Puzzle | The home price guessing game";
        }

        const p: any = property;
        const city = p.city;
        const state = p.state;

        const locationParts = [city, state].filter((part) => !!part && String(part).trim().length > 0);
        const location = locationParts.length ? locationParts.join(", ") : "";

        const bedBath = `${formatPropertyCountDisplay(p.bedrooms)} bed, ${formatPropertyCountDisplay(p.bathrooms)} bath`;

        const segments = [`PriceMe: Guess the Price of ${p.streetAddress}`];
        if (location) {
            segments.push(location);
        }
        segments.push(bedBath);

        return segments.join(" | ");
    })();

    const seoDescription = (() => {
        if (!property || !(property as any)?.streetAddress) {
            return "Play the daily home price puzzle. Guess the price in 6 tries or less!";
        }

        const p: any = property;
        const address = p.streetAddress;
        const city = p.city;
        const state = p.state;

        const locationParts = [city, state].filter((part) => !!part && String(part).trim().length > 0);
        const location = locationParts.length ? locationParts.join(", ") : "";

        const base = location ? `Guess the price of ${address} in ${location}` : `Guess the price of ${address}`;

        return `${base} in 6 tries or less. Play PriceMe and test your property pricing skills.`;
    })();
    const checkTileMode = useCallback(
        (rowIndex: number, tileIndex: number, tileValue: string) => {
            if (rowIndex === currentIndex || isEmpty(tileValue)) {
                return "default";
            }

            const correctTileValue = +(actualPriceSplit as any)[tileIndex];

            if (correctTileValue === +tileValue) {
                return "correct";
            }

            const inThreshold = inRange(
                +tileValue,
                correctTileValue - INCORRECT_THRESHOLD,
                correctTileValue + INCORRECT_THRESHOLD + 1,
            );

            return inThreshold ? "close" : "incorrect";
        },
        [actualPriceSplit, currentIndex], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const swipeHandlers = useSwipeable({
        onSwipedUp: () => !expand && setExpand(true),
        onSwipedDown: () => {
            // When swiping down on the expanded guess input section, remove focus from the input and set expand to false
            if (expand && activeTab !== "scoreboard") {
                inputRef?.current?.blur();
                setExpand(false);
            }
        },
        // NOTE: preventScrollOnSwipe is set to false because setting it to true prevents scrolling
        // in the scoreboard tab. The scoreboard contains scrollable content that users need to access.
        // IMPORTANT: If changing this value, test on mobile devices to ensure:
        // 1. Swipe gestures for expanding/collapsing the drawer still work correctly
        // 2. Scrolling in the scoreboard tab works properly
        // 3. No conflicts between swipe and scroll gestures occur
        preventScrollOnSwipe: false,
        trackMouse: true, // Optional: enables swipe events with mouse
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (ongoingAnimation !== "idle" || gameState !== GAME_STATES.ONGOING) {
            event.preventDefault();

            return;
        }

        const value = event.target.value;

        setGuesses((prevGuesses) => {
            const newGuesses = [...prevGuesses];
            newGuesses[currentIndex] = value;
            return newGuesses;
        });
    };

    const handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (ongoingAnimation !== "idle" || gameState !== GAME_STATES.ONGOING) {
            event.preventDefault();

            return;
        }

        const currentGuessLength = guesses[currentIndex]?.length ?? 0;

        if (event.code === "Enter") {
            event.preventDefault();

            if (currentGuessLength === MAX_DIGIT) {
                handleSubmit(event);
            }
        }

        // This is a fix for PM-1689 since Android Chrome has a different placement of the key code
        const keyCode = (event as any).code || (event as any).nativeEvent.key;

        if (currentGuessLength >= MAX_DIGIT && keyCode !== "Backspace") {
            event.preventDefault();
            return;
        }
    };

    const handleSubmit = (event?: any) => {
        if (gameState !== GAME_STATES.ONGOING || ongoingAnimation !== "idle") {
            event?.preventDefault?.();
            return;
        }

        sendEvent("enter_guess", {
            liveVariantVersion: currentVersion?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });

        // Mixpanel: track puzzle guess submission
        const guessNumber = (currentIndex ?? 0) + 1;
        const currentGuess = guesses[currentIndex];
        const guessAmount = currentGuess ? Number(currentGuess) : 0;

        sendMixpanelEvent("enter_guess", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            guess_number: guessNumber,
            guess_amount: guessAmount,
        });

        setIsSubmitting(true);

        // Set the ongoing animation to "transit" to prep before the next animation so that this is not "idle"
        setOngoingAnimation("transit");

        setCurrentIndex((prevIndex) => prevIndex + 1);
        if (event && event.target) {
            event.target.value = "";
        }
        inputRef.current?.focus(); // Refocus input to keep keyboard open
    };

    const propertyPlayDate = (liveVariantState as any)?.property_play_date;
    const shortLink = useMemo((): string | undefined => {
        const resolved = resolveGuestPendingShortLink({
            liveVariantState: liveVariantState as { shortLink?: string },
            property: property as { shortLink?: string; short_link?: string },
            playablePropertyId:
                liveVariantState?.playablePropertyId != null
                    ? String(liveVariantState.playablePropertyId)
                    : undefined,
        }).shortLink;
        return resolved ?? undefined;
    }, [liveVariantState, property]);

    // Human-readable property label for share text (e.g. "1969 El Dorado Ave | Berkeley")
    const propertyShareLabel = useMemo(() => buildPropertyShareLabel(property), [property]);

    const playablePropertyId = liveVariantState?.playablePropertyId;
    const playablePropertyIdStr = playablePropertyId ? String(playablePropertyId) : null;
    const puzzleStats = useQuery<PuzzleStatsResponse>({
        queryKey: ["property-puzzle-stats", playablePropertyIdStr],
        queryFn: () => getPropertyPuzzleStats(playablePropertyIdStr!),
        enabled: !!playablePropertyIdStr,
        gcTime: 0,
    });
    const scoreboardEntries = (() => {
        const guessCount = puzzleStats?.data?.guessCount ?? 0;
        const repliesCount = puzzleStats?.data?.repliesCount ?? 0;
        return Math.max(0, guessCount + repliesCount);
    })();

    const generateShareDetails = useCallback(() => {
        if (!actualPriceValue || !shortLink || !propertyPlayDate) {
            const missing = [
                !actualPriceValue && "actualPriceValue",
                !shortLink && "shortLink",
                !propertyPlayDate && "propertyPlayDate",
            ].filter(Boolean);
            logger.warn("Share details skipped: missing required data", {
                context: "Puzzle.generateShareDetails",
                missing,
                actualPriceValue,
                shortLink,
                propertyPlayDate,
            });
            return {
                shareTextTitle: "",
                shareTextBody: "",
                shareUrl: "",
            };
        }

        return buildPuzzleShareDetails({
            guesses: validGuesses,
            actualPrice: actualPriceValue,
            playDate: propertyPlayDate,
            shortLink,
            propertyLabel: propertyShareLabel,
        });
    }, [actualPriceValue, propertyPlayDate, shortLink, validGuesses, propertyShareLabel]);

    const handleBackToFeed = useCallback(() => {
        navigate(withCurrentSearchParams("/puzzle"), { replace: false });
    }, [navigate]);

    const openPuzzleShareSheet = useCallback(() => {
        const { shareTextTitle, shareTextBody, shareUrl } = generateShareDetails();
        if (!shareTextTitle) {
            return;
        }
        const propertyUrl = shortLink ? `${window.location.origin}/puzzle/${shortLink}` : null;
        openShareScoreSheet({
            shareTextTitle,
            shareTextBody,
            shareUrl,
            streak: isAuthenticated ? streakData?.currentStreak ?? null : null,
            location: "Puzzle Page",
            pageName: document.title,
            propertyUrl,
            userScore: score,
        });
    }, [
        generateShareDetails,
        isAuthenticated,
        openShareScoreSheet,
        score,
        shortLink,
        streakData?.currentStreak,
    ]);

    const clearGuestScoreboardAuthDelay = useCallback(() => {
        setGuestScoreboardAuthNotBeforeMs(0);
    }, []);

    const guestAuthRedirectPath = useMemo(
        () => appendPuzzleViewToPath(`${location.pathname}${location.search}`, "scoreboard"),
        [location.pathname, location.search],
    );

    const { handleEmail: handleScoreboardSaveRegisterEmail } = useOneTapAuth({
        redirectPath: guestAuthRedirectPath,
        version: "puzzle",
        location: MIXPANEL_REGISTER_LOCATION_SCOREBOARD_SAVE,
    });

    const { handleEmail: handleStreakModalSaveRegisterEmail } = useOneTapAuth({
        redirectPath: guestAuthRedirectPath,
        version: "puzzle",
        location: MIXPANEL_REGISTER_LOCATION_STREAK_MODAL,
    });

    const handleGuestSaveIntent = useCallback(() => {
        if (isAuthenticated) {
            return;
        }
        const playablePropertyId =
            liveVariantState?.playablePropertyId != null
                ? String(liveVariantState.playablePropertyId)
                : undefined;
        const guessNumeric = (validGuesses as unknown[])
            .filter((g) => !isEmpty(g))
            .map((g) => parseInt(String(g).replace(/\D/g, ""), 10))
            .filter((n: number) => Number.isFinite(n) && n > 0);
        const { shortLink: shortLinkResolved } = resolveGuestPendingShortLink({
            liveVariantState: liveVariantState as { shortLink?: string },
            property: property as { shortLink?: string; short_link?: string },
            playablePropertyId,
        });
        if (!shortLinkResolved) {
            return;
        }
        try {
            sessionStorage.setItem(PUZZLE_OPEN_SCOREBOARD_KEY, "true");
        } catch {
            /* ignore */
        }
        persistGuestPuzzleCompletionToPendingStore({
            playablePropertyId,
            shortLink: shortLinkResolved,
            guesses: guessNumeric,
            isArchivePlay: Boolean(!(liveVariantState as any)?.same_with_current_date),
        });
    }, [isAuthenticated, liveVariantState, property, validGuesses]);

    const persistRegisterLocation = useCallback((registerLocation: string) => {
        try {
            sessionStorage.setItem(PUZZLE_REGISTER_LOCATION_KEY, registerLocation);
        } catch {
            /* ignore */
        }
    }, []);

    /** Scoreboard row Save it: persist guest score then Auth0 email signup. */
    const handleGuestSaveFromScoreboard = useCallback(() => {
        handleGuestSaveIntent();
        persistRegisterLocation(MIXPANEL_REGISTER_LOCATION_SCOREBOARD_SAVE);
        handleScoreboardSaveRegisterEmail();
    }, [handleGuestSaveIntent, handleScoreboardSaveRegisterEmail, persistRegisterLocation]);

    /** Streak modal Save it: persist guest score then Auth0 email signup. */
    const handleGuestSaveFromStreakModal = useCallback(() => {
        handleGuestSaveIntent();
        persistRegisterLocation(MIXPANEL_REGISTER_LOCATION_STREAK_MODAL);
        handleStreakModalSaveRegisterEmail();
    }, [handleGuestSaveIntent, handleStreakModalSaveRegisterEmail, persistRegisterLocation]);

    const closeGuestPrompt = useCallback(() => {
        setBannerStep(1); // Reset to step 1 when closing
        setShowGuestAuthPrompt(false);
        setBannerActuallyVisible(false); // Clear immediately since no exit animation when closing manually
        localStorage.removeItem("puzzle-one-tap-active");
    }, []);

    // Callback when banner exit animation completes
    const onBannerExitComplete = useCallback(() => {
        setBannerActuallyVisible(false);
    }, []);

    // Update bannerActuallyVisible when showGuestAuthPrompt changes
    useEffect(() => {
        if (showGuestAuthPrompt) {
            setBannerActuallyVisible(true);
        }
        // Note: setBannerActuallyVisible(false) is handled by:
        // 1. onBannerExitComplete (when exit animation completes)
        // 2. closeGuestPrompt (when banner is closed/dismissed immediately)
    }, [showGuestAuthPrompt]);

    // Clear bannerActuallyVisible when user becomes authenticated (banner unmounts without animation)
    useEffect(() => {
        if (isAuthenticated || userState?.is_created) {
            setBannerActuallyVisible(false);
            setGuestScoreboardAuthNotBeforeMs(0);
        }
    }, [isAuthenticated, userState?.is_created]);

    // Trigger guest auth prompt on puzzle load (drawer for mobile, modal for desktop)
    useEffect(() => {
        if (isAuthenticated || userState?.is_created) {
            closeGuestPrompt();
            return;
        }

        const hasSeen = sessionStorage.getItem("puzzle-one-tap-shown");
        const isActive = localStorage.getItem("puzzle-one-tap-active") === "true";

        // If another page has already activated the one-tap prompt in this browser
        // session, keep it active here without re-triggering the tracking flags.
        if (isActive) {
            setShowGuestAuthPrompt(true);
            return;
        }

        if (!hasSeen) {
            setShowGuestAuthPrompt(true);
            sessionStorage.setItem("puzzle-one-tap-shown", "true");
            localStorage.setItem("puzzle-one-tap-active", "true");
        }
    }, [isAuthenticated, userState?.is_created, closeGuestPrompt]);

    const handleShowFullListing = () => {
        sendEvent("see_full_listing_daily_property", {
            liveVariantVersion: currentVersion?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });

        sendMixpanelEvent("External Link Click", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            externalUrl: url,
            externalClickType: "full",
        });

        if (isPropertyGuessed) {
            window.open(url, "_blank");
        } else {
            setShowFullListingModal(true);
        }
    };

    const handleFullListingModalClicks = (eventName: string) => {
        sendEvent(eventName, {
            liveVariantVersion: currentVersion?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });

        if (eventName === "back_to_guess_price") {
            setShowFullListingModal(false);
        }

        if (eventName === "see_full_listing_modal") {
            // Mixpanel: external link click from give up modal
            sendMixpanelEvent("External Link Click", {
                pageUrl: window.location.href,
                email: userState?.emailAddress || null,
                nickname: userState?.nickname || null,
                externalUrl: url,
                externalClickType: "giveup",
            });
            window.open(url, "_blank");
        }
    };

    const getScoreFeedback = (score: number) => {
        switch (score) {
            case 1:
                return "Unbelievable!";
            case 2:
                return "Uncanny!";
            case 3:
                return "Wow! Strong performance!";
            case 4:
                return "Well done";
            case 5:
                return "Solid effort today";
            case 6:
                return "Phew!";
            default:
                return `${formatPrice(actualPriceValue as any)} \n Try again tomorrow`;
        }
    };

    // Check property status and validate images
    useEffect(() => {
        if (!property) return;

        const { imageUrls } = property as any;

        if (!imageUrls || (imageUrls as any).length === 0) {
            setShowUnavailablePuzzle(true);
            setIsPhotosVerified(true); // Mark as verified to prevent infinite loading
            return;
        }
        if (!isPhotosVerified) {
            // Validate image URLs if photos are available
            Promise.all(imageUrls.map(validateImageURL))
                .then((results) => {
                    const validUrls = results.filter((result) => result.isValid).map((result) => result.url);

                    if (isEmpty(validUrls)) {
                        setShowUnavailablePuzzle(true);
                        setIsPhotosVerified(true); // Mark as verified even when no valid images to prevent infinite loading
                    } else {
                        setShowUnavailablePuzzle(false);
                        setProperty({ ...property, imageUrls: validUrls });
                        setIsPhotosVerified(true);
                    }
                })
                .catch((error) => {
                    console.error("Error validating images:", error);
                    logger.error("Error validating images", error, {
                        context: "Puzzle",
                        propertyId: (property as any)?.id,
                    });
                    setShowUnavailablePuzzle(true);
                    setIsPhotosVerified(true); // Mark as verified on error to prevent infinite loading
                });
        }
    }, [property, isPhotosVerified]);

    const navigateToNextPuzzleListing = useCallback(async () => {
        const playablePropertyId = liveVariantState?.playablePropertyId;
        if (playablePropertyId != null) {
            const playableIdStr = String(playablePropertyId);
            if (!unavailableAutoSkipExcludedRef.current.includes(playableIdStr)) {
                unavailableAutoSkipExcludedRef.current = [...unavailableAutoSkipExcludedRef.current, playableIdStr];
            }
        }
        const feedUserId = userState?.id ? String(userState.id) : undefined;
        const nextProperty = await fetchNextPlayableProperty({
            userId: feedUserId,
            requestingUserId: feedUserId,
            slc: puzzleFeedSlc,
            queryClient,
            currentPlayablePropertyId: playablePropertyId ?? null,
            currentPropertyId: property?.id,
            guessedPropertyIds: guessedPropertiesLocal ?? [],
            guessedPropertyResults: (guessedPropertyResultsLocal ?? []) as PuzzleGuessedPropertyResult[],
            versionStored: version,
            excludedPlayablePropertyIds: unavailableAutoSkipExcludedRef.current,
        });
        if (!nextProperty) {
            setIsRedirectingFromUnavailablePuzzle(false);
            setUnavailableAutoSkipNoMorePuzzles(true);
            toast.error("No more puzzles available right now.");
            return;
        }
        const listingPath = buildPuzzleListingPathFromNextItem(nextProperty);
        if (!listingPath) {
            setIsRedirectingFromUnavailablePuzzle(false);
            setUnavailableAutoSkipNoMorePuzzles(true);
            toast.error("Unable to open the next puzzle.");
            return;
        }
        flushSync(() => {
            setActiveTab("guesses");
            setExpand(true);
            setScoreboardViewLocation("Puzzle Drawer");
        });
        navigate(buildPuzzleListingNavigationUrl(listingPath));
    }, [
        guessedPropertiesLocal,
        guessedPropertyResultsLocal,
        liveVariantState?.playablePropertyId,
        navigate,
        property?.id,
        puzzleFeedSlc,
        queryClient,
        userState?.id,
        version,
    ]);

    useEffect(() => {
        if (!showUnavailablePuzzle && isPhotosVerified && property && !showRemovedPuzzle) {
            unavailableAutoSkipAttemptsRef.current = 0;
            unavailableAutoSkipExcludedRef.current = [];
            setIsRedirectingFromUnavailablePuzzle(false);
            setUnavailableAutoSkipNoMorePuzzles(false);
        }
    }, [isPhotosVerified, property, showRemovedPuzzle, showUnavailablePuzzle]);

    useEffect(() => {
        if (!showUnavailablePuzzle || showRemovedPuzzle) {
            setIsRedirectingFromUnavailablePuzzle(false);
            setUnavailableAutoSkipNoMorePuzzles(false);
            return undefined;
        }
        if (unavailableAutoSkipAttemptsRef.current >= MAX_UNAVAILABLE_AUTO_SKIP_ATTEMPTS) {
            setUnavailableAutoSkipNoMorePuzzles(true);
            return undefined;
        }
        let isCancelled = false;
        const redirectTimerId = window.setTimeout(() => {
            if (isCancelled) {
                return;
            }
            unavailableAutoSkipAttemptsRef.current += 1;
            setIsRedirectingFromUnavailablePuzzle(true);
            void navigateToNextPuzzleListing().catch((error) => {
                logger.error("Auto-skip from unavailable puzzle failed", error, {
                    context: "Puzzle.navigateToNextPuzzleListing",
                    playablePropertyId: liveVariantState?.playablePropertyId,
                });
                setIsRedirectingFromUnavailablePuzzle(false);
                toast.error("Unable to load the next puzzle. Please try again.");
            });
        }, UNAVAILABLE_OOPS_DISPLAY_MS);
        return () => {
            isCancelled = true;
            window.clearTimeout(redirectTimerId);
        };
    }, [liveVariantState?.playablePropertyId, navigateToNextPuzzleListing, showRemovedPuzzle, showUnavailablePuzzle]);

    const hasUnavailablePuzzleState = showRemovedPuzzle || showUnavailablePuzzle;

    // Clear stale WIN/LOSE only when navigating to a different playable property, not on every ref mismatch.
    useEffect(() => {
        const currentPlayableId = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
        const previousPlayableId = prevPlayablePropertyIdForGameRef.current;
        if (currentPlayableId != null) {
            if (
                previousPlayableId != null &&
                previousPlayableId !== currentPlayableId &&
                gameState !== GAME_STATES.ONGOING &&
                completionPlayablePropertyIdRef.current !== currentPlayableId
            ) {
                setGameState(GAME_STATES.ONGOING);
                completionPlayablePropertyIdRef.current = null;
            }
            prevPlayablePropertyIdForGameRef.current = currentPlayableId;
        }
    }, [gameState, liveVariantState?.playablePropertyId]);

    useEffect(() => {
        if (gameState === GAME_STATES.ONGOING) {
            return;
        }
        const currentPlayableId = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
        if (!currentPlayableId || completionPlayablePropertyIdRef.current !== currentPlayableId) {
            return;
        }
        if (ongoingAnimation !== "idle" || arePuzzleTooltipsVisible(puzzleTooltipData)) {
            return;
        }
        // Auto-open the scoreboard only once per completion, so if the user goes back to the
        // listing/guesses view we don't yank them back to the scoreboard again.
        if (hasAutoOpenedScoreboardRef.current) {
            return;
        }
        hasAutoOpenedScoreboardRef.current = true;
        if (!guessedPropertiesLocal?.includes(liveVariantState?.playablePropertyId)) {
            setGuessedProperties([...guessedPropertiesLocal, liveVariantState?.playablePropertyId]);
        }

        // Store the result in guessed_property_results. Keyed by playablePropertyId + property_play_date + version
        // so we never show another day's guesses, while allowing multiple properties per same date.
        const playDate = (liveVariantState as any)?.property_play_date;
        setGuessedPropertyResults([
            ...(guessedPropertyResultsLocal as any[]),
            {
                playablePropertyId: liveVariantState?.playablePropertyId,
                property_play_date: playDate,
                property,
                guesses,
                currentIndex,
                version,
                shortLink: (liveVariantState as any)?.shortLink ?? (property as any)?.shortLink ?? null,
                sameWithCurrentDate: (liveVariantState as any)?.same_with_current_date,
            },
        ]);

        if (!isAuthenticated) {
            const guessNumeric = (validGuesses as unknown[])
                .filter((g) => !isEmpty(g))
                .map((g) => parseInt(String(g).replace(/\D/g, ""), 10))
                .filter((n: number) => Number.isFinite(n) && n > 0);
            persistGuestPuzzleCompletionToPendingStore({
                playablePropertyId:
                    liveVariantState?.playablePropertyId != null
                        ? String(liveVariantState.playablePropertyId)
                        : undefined,
                shortLink: resolveGuestPendingShortLink({
                    liveVariantState: liveVariantState as { shortLink?: string },
                    property: property as { shortLink?: string; short_link?: string },
                    playablePropertyId:
                        liveVariantState?.playablePropertyId != null
                            ? String(liveVariantState.playablePropertyId)
                            : undefined,
                }).shortLink,
                guesses: guessNumeric,
                isArchivePlay: Boolean(!(liveVariantState as any)?.same_with_current_date),
            });
        }

        // Guest: streak signup interstitial at most once per session. Registered: driven by submitGuess streak in mutation onSuccess.
        if (!isAuthenticated && !hasGuestStreakModalBeenShownThisSession()) {
            markGuestStreakModalShownThisSession();
            setStreakData({
                currentStreak: 1,
                previousStreak: 0,
                streakChangeType: "guest_static",
            });
            pendingShowStreakModalRef.current = true;
        }

        const scoreFeedback = getScoreFeedback(score);
        toast(scoreFeedback, { duration: isPuzzleLose ? Infinity : 1500 });
        setIsToastDisplayed(true);
        setInputFocused(false); // Remove the input focus after the game (fixes https://app.asana.com/0/1204535088114623/1208937585206254/f)

        scoreboardAutoOpenPendingRef.current = true;
        setScoreboardViewLocation("Auto");
        setActiveTab("scoreboard");
        setPuzzleView("scoreboard", { replace: true });
    }, [gameState, liveVariantState?.playablePropertyId, ongoingAnimation, puzzleTooltipData, setPuzzleView]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!pendingShowStreakModalRef.current || !streakData) {
            return;
        }
        if (!hasAutoOpenedScoreboardRef.current || activeTab !== "scoreboard") {
            return;
        }
        pendingShowStreakModalRef.current = false;
        setShowStreakModal(true);
    }, [activeTab, streakData]);

    useEffect(() => {
        if (isPropertyGuessed && !isToastDisplayed && !isLoading && isPuzzleLose) {
            toast(getScoreFeedback(score), { duration: Infinity });
            setIsToastDisplayed(true);
        }

        const isGameInProgress = gameState === GAME_STATES.ONGOING;
        if (!isPropertyGuessed && !isSavingUserGuess && isGameInProgress) {
            toast.dismiss();
            setIsToastDisplayed(false);
        }
    }, [gameState, isPropertyGuessed, isToastDisplayed, isLoading, isSavingUserGuess, score]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync guesses from API/localStorage when loading an already-played puzzle. Do NOT sync when game is
    // already over (WIN/LOSE) so we never overwrite local state with stale/empty refetch data after submit,
    // which could blank the tiles and cause duplicate submissions
    useEffect(() => {
        if (suppressGuessHydrationRef.current) {
            suppressGuessHydrationRef.current = false;
            return;
        }
        if (gameState !== GAME_STATES.ONGOING || !isPropertyGuessed || !playablePropertyIdForGuessed) {
            return;
        }
        const storedResult = guessedResult as
            | { playablePropertyId?: string | number; guesses?: GuessValue[]; currentIndex?: number }
            | undefined;
        if (!storedResult?.guesses) {
            return;
        }
        if (
            !isAuthenticated &&
            String(storedResult.playablePropertyId ?? "") !== String(playablePropertyIdForGuessed)
        ) {
            return;
        }
        const localFilledGuesses = guesses.filter((guessValue) => !isEmpty(guessValue));
        const storedFilledGuesses = storedResult.guesses.filter((guessValue) => !isEmpty(guessValue));
        if (localFilledGuesses.length > 0 && localFilledGuesses.length !== storedFilledGuesses.length) {
            return;
        }
        if (!isEqual(guesses, storedResult.guesses)) {
            setGuesses(storedResult.guesses);
            setCurrentIndex(storedResult.currentIndex ?? 0);
        }
    }, [
        gameState,
        guessedResult,
        guesses,
        isAuthenticated,
        isPropertyGuessed,
        playablePropertyIdForGuessed,
        variantLink,
    ]);

    // Lock app scrolling while guess input is focused/active.
    useScrollLock({
        lock: inputFocused && inputRef.current === document.activeElement,
        isMobile,
        containerSelector: ".app-container",
    });

    useEffect(() => {
        if (!dailyProperties?.isLoading && !dailyProperties?.isFetching && !property) {
            // try to refetch when property is not yet set
            dailyProperties.refetch();
        }
    }, [property]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Function to handle viewport resize issues for iOS
        const handleResize = () => {
            const currentViewportHeight = window.innerHeight;
            setVirtualKeyboardShown(currentViewportHeight < viewportHeight);
            setViewportHeight(currentViewportHeight);
        };

        const handleKeyboardChange = (isKeyboardOpen: boolean | string | null | undefined) => {
            let keyboardState = false;

            if (isNil(isKeyboardOpen)) {
                keyboardState = false; // Default to false when it's null or undefined
            } else if (isKeyboardOpen === true || isKeyboardOpen === "visible") {
                keyboardState = true; // Keyboard is visible
            } else if (isKeyboardOpen === false || isKeyboardOpen === "hidden") {
                keyboardState = false; // Keyboard is hidden
            }

            setVirtualKeyboardShown(keyboardState);
        };

        // Add a keyboard detector if supported
        let unsubscribe: any;
        if (isSupported()) {
            unsubscribe = subscribe((isKeyboardOpen: any) => {
                handleKeyboardChange(isKeyboardOpen);
            });
        }

        // iOS-specific resize handling / quirks
        if (isIOS() && !isSupported()) {
            window.addEventListener("resize", handleResize);
        }

        // Cleanup
        return () => {
            if (unsubscribe) unsubscribe();
            if (isIOS()) {
                window.removeEventListener("resize", handleResize);
            }
        };
    }, [viewportHeight]);

    useEffect(() => {
        // There is a weird delay in the viewport resize event in IOS when the virtual keyboard is opened after the page load
        // This delay goes away when the user scrolls the page
        // https://stackoverflow.com/questions/72747030/delay-on-resize-event-on-virtual-keyboard-on-ios-safari
        const obstructionRem = virtualKeyboardShown ? pxToRem(getKeyboardObstructionPx()) : 0;
        setGuessInputHeight(
            expand
                ? EXPANDED_CONTENT_BASE_HEIGHT_REM +
                      EXPANDED_TOP_OFFSET_REM +
                      obstructionRem
                : INITIAL_GUESS_INPUT_HEIGHT,
        );
    }, [
        viewport?.height,
        viewport?.width,
        expand,
        isPropertyGuessed,
        virtualKeyboardShown,
        activeTab,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isLoading && !shownHelpModals?.includes(version) && !hasUnavailablePuzzleState) {
            setShownHelpModal([...(shownHelpModals as any), version]);
            setModalId("LIVE_VARIANT_HELP_MODAL");
        }
    }, [isLoading, shownHelpModals, hasUnavailablePuzzleState]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (property) {
            sendEvent("start_page_load", {
                liveVariantVersion: currentVersion?.gaVersion,
                visitorStatus: visitorStatus || "new",
            });
        }
    }, [property]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve WIN/LOSE only after the submitted row's tile reveal animation has finished.
    useEffect(() => {
        if (
            !isSubmitting ||
            gameState !== GAME_STATES.ONGOING ||
            ongoingAnimation !== "idle" ||
            currentIndex <= 0 ||
            actualPriceValue === undefined
        ) {
            return;
        }

        const submittedGuess = Number(guesses[currentIndex - 1]);
        if (!Number.isFinite(submittedGuess)) {
            return;
        }

        const isCorrect = submittedGuess === actualPriceValue;
        const noMoreGuess = currentIndex >= guesses.length;

        if (isCorrect) {
            completionPlayablePropertyIdRef.current = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
            setGameState(GAME_STATES.WIN);
            return;
        }

        if (noMoreGuess) {
            completionPlayablePropertyIdRef.current = toPlayablePropertyIdKey(liveVariantState?.playablePropertyId);
            setGameState(GAME_STATES.LOSE);
        }
    }, [
        isSubmitting,
        gameState,
        ongoingAnimation,
        currentIndex,
        actualPriceValue,
        guesses,
        liveVariantState?.playablePropertyId,
    ]);

    useEffect(() => {
        if (gameState !== GAME_STATES.ONGOING || ongoingAnimation !== "transit") {
            return undefined;
        }
        const fallbackTimerId = window.setTimeout(() => {
            setOngoingAnimation("idle");
        }, TILE_ROW_FLIP_ANIMATION_MS);
        return () => window.clearTimeout(fallbackTimerId);
    }, [gameState, ongoingAnimation]);

    // Prevent shrinking the guess input container while animation/tooltip is still active.
    useEffect(() => {
        const noTooltipShown = !arePuzzleTooltipsVisible(puzzleTooltipData);

        if (isSubmitting && ongoingAnimation === "idle" && noTooltipShown) {
            setIsSubmitting(false);
        }
    }, [puzzleTooltipData, ongoingAnimation, isSubmitting]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track Mixpanel "Puzzle Score" event when puzzle is completed
    useEffect(() => {
        if ((gameState === GAME_STATES.WIN || gameState === GAME_STATES.LOSE) && !hasTrackedPuzzleScoreRef.current) {
            // Calculate user_score: 1-6 if solved, 7 if failed
            const correctGuessIndex = guesses.findIndex((guess) => +(guess as any) === actualPriceValue);
            const userScore = correctGuessIndex !== -1 ? correctGuessIndex + 1 : 7; // 1-6 if solved, 7 if failed

            sendMixpanelEvent("Puzzle Score", {
                pageUrl: window.location.href,
                email: userState?.emailAddress || null,
                nickname: userState?.nickname || null,
                userScore: userScore,
            });

            hasTrackedPuzzleScoreRef.current = true;
        }
    }, [gameState, guesses, actualPriceValue, userState?.emailAddress, userState?.nickname, sendMixpanelEvent]);

    useEffect(() => {
        if (property && !isLoading && !hasUnavailablePuzzleState) {
            setLiveVariantState({
                ...(liveVariantState as any),
                show_date_picker_tooltip: !(liveVariantState as any).same_with_current_date,
            });
        }
    }, [property, isLoading, hasUnavailablePuzzleState]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isPropertyGuessed && !isEmpty(validGuesses)) {
            const dateToday = (moment.tz as any)(new Date(), "America/Chicago").format("YYYY-MM-DD");
            const shareDetails = generateShareDetails();

            if (propertyPlayDate === dateToday && !isEqual(puzzleShareObject, shareDetails)) {
                setPuzzleShareObject(shareDetails);
            }
        }
    }, [
        isAuthenticated,
        isPropertyGuessed,
        propertyPlayDate,
        puzzleShareObject,
        validGuesses,
        generateShareDetails,
        setPuzzleShareObject,
    ]);

    const renderActionButtons = () => (
        <>
            <div className="tw-flex tw-items-center tw-pt-2 tw-justify-center tw-gap-2 tw-rounded-full tw-overflow-hidden tw-bg-[#2E2E2E]">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        // Use game completion state as the primary source of truth. Guest completion persistence
                        // is written after animations settle; during that window, `isPropertyGuessed` can lag.
                        if (isGameCompleted || isPropertyGuessed) {
                            openPuzzleShareSheet();
                        } else {
                            setInputFocused(true);
                            setActiveTab("guesses");
                            flushSync(() => {
                                setExpand(true);
                            });
                            inputRef.current?.focus();
                        }
                    }}
                    className="tw-flex tw-flex-1 tw-items-center tw-h-[37px] tw-justify-center tw-gap-1 tw-py-1 tw-px-[24px] tw-rounded-full tw-font-medium hover:tw-opacity-90 tw-border-0 tw-cursor-pointer tw-bg-[#F69929] tw-text-white"
                    data-cy="puzzle-guess-price-button"
                >
                    <IoCalculatorOutline className="tw-shrink-0" fontSize={16} />
                    <span>{isGameCompleted || isPropertyGuessed ? "Share Score" : "Guess Price"}</span>
                </button>
            </div>
        </>
    );

    return (
        <>
            {/* Desktop: Use modal overlay and keep it mounted; use internal `show` prop to drive animations. */}
            {!isMobile && (
                <OneTapSignInModal
                    show={showGuestAuthPrompt}
                    onClose={closeGuestPrompt}
                    redirectPath="/puzzle"
                    version="puzzle"
                    location={MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL}
                />
            )}

            <PageHead
                title={seoTitle}
                description={seoDescription}
                realEstateListing={
                    property && (property as any)?.streetAddress
                        ? {
                              name: (property as any).fullAddress || (property as any).streetAddress,
                              streetAddress: (property as any).streetAddress,
                              addressLocality: (property as any).city,
                              addressRegion: (property as any).state,
                              postalCode: (property as any).zipCode,
                              price: Number(getDisplayPrice(property)),
                          }
                        : undefined
                }
            />

            {streakData && (
                <StreakModal
                    show={showStreakModal}
                    currentStreak={streakData.currentStreak}
                    previousStreak={streakData.previousStreak}
                    streakChangeType={streakData.streakChangeType}
                    isGuest={!isAuthenticated}
                    onGuestSaveRegister={handleGuestSaveFromStreakModal}
                    onClose={(_dismissType) => {
                        setShowStreakModal(false);
                        setScoreboardViewLocation("Auto");
                        setActiveTab("scoreboard");
                        if (!isAuthenticated && !userState?.is_created) {
                            setGuestScoreboardAuthNotBeforeMs(Date.now() + GUEST_SCOREBOARD_AUTH_NOT_BEFORE_AFTER_STREAK_MS);
                        }
                    }}
                />
            )}

            {(() => {
                const dailyPropertyError = (dailyProperties as any)?.error;

                if (dailyPropertyError && !showRemovedPuzzle) {
                    return (
                        <div className="tw-flex tw-items-center tw-justify-center tw-h-3/4 tw-p-8">
                            {dailyPropertyError.response?.data?.message || "Failed to load daily property. Please try again later."}
                        </div>
                    );
                }

                if (hasUnavailablePuzzleState) {
                    const isAutoSkippingUnavailable =
                        showUnavailablePuzzle && !showRemovedPuzzle && isRedirectingFromUnavailablePuzzle;
                    const canAutoSkipUnavailable =
                        showUnavailablePuzzle &&
                        !showRemovedPuzzle &&
                        !unavailableAutoSkipNoMorePuzzles &&
                        unavailableAutoSkipAttemptsRef.current < MAX_UNAVAILABLE_AUTO_SKIP_ATTEMPTS;
                    return (
                        <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-m-10 tw-gap-4">
                            {isAutoSkippingUnavailable ? (
                                <>
                                    <MoonLoader size={40} color="#63c19f" />
                                    <p className="tw-max-w-[400px] tw-text-white tw-text-base">
                                        Finding your next puzzle…
                                    </p>
                                </>
                            ) : (
                                <>
                            <h2>{showRemovedPuzzle ? REMOVED_PUZZLE_MESSAGE : "Oops."}</h2>
                            <p className="tw-max-w-[600px]">
                                Sometimes a listing agent updates a property after it sells, and we can't show it
                                anymore. This seems to be the case now.
                            </p>
                                    {canAutoSkipUnavailable && (
                                        <p className="tw-max-w-[400px] tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-400">
                                            Taking you to another puzzle in a moment
                                            <MoonLoader size={16} color="#63c19f" aria-label="Loading" />
                                        </p>
                                    )}
                                    {unavailableAutoSkipNoMorePuzzles && (
                                        <p className="tw-max-w-[400px] tw-text-sm tw-text-gray-400">
                                            No more puzzles available right now.
                                        </p>
                                    )}
                                </>
                            )}

                            {hasFeedReturnAnchor && !isAutoSkippingUnavailable && (
                                <CustomButton
                                    className="tw-mt-4 tw-bg-[#6C26C4] tw-text-white tw-rounded-full tw-px-6 tw-py-3 tw-text-base"
                                    text="Back to feed"
                                    handleClick={handleBackToFeed}
                                    dataCy="puzzle-404-back-button"
                                    id="puzzle-404-back-button"
                                />
                            )}
                        </div>
                    );
                }

                if (isLoading) {
                    return (
                        <div className="tw-flex tw-items-center tw-justify-center tw-h-3/4">
                            <MoonLoader size={40} color="#63c19f" />
                        </div>
                    );
                }

                return (
                    <div className="tw-flex tw-flex-row tw-w-full tw-justify-center tw-gap-4">
                        {/* Left Sidebar Ad - Desktop Only */}
                        {!isMobile && (
                            <div className="tw-flex-shrink-0 tw-w-[300px] tw-max-w-[385px] tw-pl-4">
                                <div className="tw-sticky tw-top-[calc(9svh+2rem)] tw-w-full tw-z-10">
                                    {/* <AdSenseAd
                                        slot={config.ADSENSE_PUZZLE_SIDEBAR_SLOT_ID}
                                        format="vertical"
                                    /> */}
                                </div>
                            </div>
                        )}

                        {/* Main Content */}
                        <div
                            className="property-container puzzle-wide-content tw-flex-1 tw-max-w-full tw-relative md:tw-max-w-[728px] md:tw-mx-auto"
                            ref={bodyRef}
                        >
                            {/* Mobile: Auth banner below sticky tabs */}
                            {isMobile && (
                                <OneTapSignInBanner
                                    show={showGuestAuthPrompt}
                                    onClose={closeGuestPrompt}
                                    redirectPath="/puzzle"
                                    version="puzzle"
                                    onStepChange={setBannerStep}
                                    location={MIXPANEL_REGISTER_LOCATION_MOBILE_PUZZLE_BANNER}
                                    onExitComplete={onBannerExitComplete}
                                />
                            )}

                            {activeTab === "guesses" ? (
                                <>
                                    <Toggle
                                        activeTab={activeTab}
                                        onTabChange={handlePuzzleTabChange}
                                        scoreboardCount={scoreboardEntries}
                                        isMobile={isMobile}
                                        placement="sticky-pill"
                                    />
                                    <PropertyInfo
                                        property={property as any}
                                        showFullListing={handleShowFullListing}
                                        onBackClick={hasFeedReturnAnchor ? handleBackToFeed : undefined}
                                        showBanner={isMobile && bannerActuallyVisible}
                                        bannerStep={bannerStep}
                                        puzzleView={puzzleView}
                                        onPuzzleViewChange={(view) => setPuzzleView(view, { replace: true })}
                                        puzzleMediaRestoreView={
                                            puzzleView === "scoreboard" ? "scoreboard" : "listing"
                                        }
                                        imageSliderOverlay={
                                            playablePropertyIdStr ? (
                                                <PuzzleListingPropertyLike
                                                    playablePropertyId={playablePropertyIdStr}
                                                    nicknameLiked={
                                                        (property as { listingAgent?: { nickname?: string | null } })
                                                            ?.listingAgent?.nickname ?? null
                                                    }
                                                />
                                            ) : undefined
                                        }
                                    />

                                    <div id="puzzle-guess-input-bar" className="tw-z-[97] tw-fixed tw-bottom-0 tw-left-0 tw-right-0">
                                            <div
                                                onClick={() => {
                                                    if (!expand) {
                                                        setInputFocused(true);
                                                        flushSync(() => {
                                                            setExpand(true);
                                                        });
                                                        inputRef.current?.focus();
                                                    } else {
                                                        setExpand(true);
                                                    }
                                                }}
                                            >
                                                <motion.div
                                                    className="!tw-bg-[#2E2E2E] guess-input-container !tw-overflow-visible px-3 py-0 guess-input-container-shadow tw-rounded-t-md"
                                                    {...swipeHandlers} // Attach swipe handlers here
                                                    initial={{ height: `${INITIAL_GUESS_INPUT_HEIGHT}rem` }}
                                                    animate={{
                                                        height: `${guessInputHeight}rem`,
                                                    }}
                                                    transition={{
                                                        height: {
                                                            ease: "easeOut",
                                                            duration: 0.5,
                                                            delay: virtualKeyboardShown ? 0 : 0.1,
                                                        },
                                                    }}
                                                >
                                                    {/* Collapsed: pill with (Guess Price / Share Score) + Scoreboard */}
                                                    {!expand && (
                                                        <div className="tw-flex tw-flex-col tw-w-full tw-h-full tw-justify-center">
                                                            {isPropertyGuessed && (
                                                                <div className="flex-wrap d-flex align-items-center justify-content-center tw-pt-2">
                                                                    <IoChevronUp
                                                                        className="tw-text-[#F69929]"
                                                                        fontSize={25}
                                                                    />
                                                                </div>
                                                            )}
                                                            {renderActionButtons()}
                                                        </div>
                                                    )}

                                                    {expand && (
                                                        <>
                                                            <div className="flex-wrap d-flex align-items-center justify-content-center">
                                                                <motion.div
                                                                    className="score-to-beat mw-100 px-4"
                                                                    initial={false}
                                                                    animate={{ rotate: expand ? 180 : 0 }}
                                                                    transition={{
                                                                        ease: "easeOut",
                                                                        duration: 0.5,
                                                                        delay: 0.1,
                                                                    }}
                                                                >
                                                                    <IoChevronUp
                                                                        className="hover:tw-cursor-pointer tw-text-[#F69929]"
                                                                        fontSize={25}
                                                                        onClick={(e) => {
                                                                            // We need to stop the propagation to the Sticky component's onClick handler if expanded
                                                                            if (expand) {
                                                                                e.stopPropagation();
                                                                                setActiveTab("guesses");
                                                                            }

                                                                            setExpand(!expand);
                                                                        }}
                                                                    />
                                                                </motion.div>
                                                            </div>

                                                            {/* Tab Content */}

                                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-py-4">
                                                                {guesses.map((guess, index) => (
                                                                    <TileRow
                                                                        key={`tile-row-${playablePropertyIdForGuessed ?? variantLink}-${index}`}
                                                                        expand={expand}
                                                                        length={MAX_DIGIT}
                                                                        rowIndex={index}
                                                                        value={guess as any}
                                                                        gameIndex={currentIndex}
                                                                        checkTileMode={checkTileMode}
                                                                        inputRef={inputRef}
                                                                        handleSubmit={handleSubmit}
                                                                        setOngoingAnimation={setOngoingAnimation}
                                                                        ongoingAnimation={ongoingAnimation}
                                                                        isPropertyGuessed={isPropertyGuessed}
                                                                        puzzleTooltipData={puzzleTooltipData}
                                                                        setPuzzleTooltipData={setPuzzleTooltipData}
                                                                    />
                                                                ))}

                                                                <input
                                                                    type="number"
                                                                    inputMode="numeric"
                                                                    className="tw-opacity-0 tw-fixed tw--left-[2000px]"
                                                                    disabled={
                                                                        isPropertyGuessed ||
                                                                        gameState !== GAME_STATES.ONGOING
                                                                    }
                                                                    ref={inputRef}
                                                                    value={guesses[currentIndex] || ""}
                                                                    maxLength={MAX_DIGIT as any}
                                                                    onChange={handleChange}
                                                                    onKeyDown={handleEnterKey}
                                                                    onFocus={() => setInputFocused(true)}
                                                                    onBlur={() => setInputFocused(false)}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </motion.div>
                                            </div>
                                    </div>
                                    {showFullListingModal && (
                                        <FullListingModal
                                            show={showFullListingModal}
                                            onClick={handleFullListingModalClicks}
                                        />
                                    )}
                                </>
                            ) : (
                                <div className="property-map-container tw-relative tw-justify-center tw-h-svh tw-bg-[#1a1a2e]">
                                    <Toggle
                                        activeTab={activeTab}
                                        onTabChange={handlePuzzleTabChange}
                                        scoreboardCount={scoreboardEntries}
                                        showBanner={isMobile && bannerActuallyVisible}
                                        isMobile={isMobile}
                                        bannerStep={bannerStep}
                                        placement="scoreboard-bar"
                                        onBackClick={hasFeedReturnAnchor ? handleBackToFeed : undefined}
                                    />
                                    <ScoreboardFeed
                                        isPropertyGuessed={isPropertyGuessed || isGameCompleted}
                                        playablePropertyId={
                                            liveVariantState?.playablePropertyId
                                                ? String(liveVariantState.playablePropertyId)
                                                : undefined
                                        }
                                        isActive={activeTab === "scoreboard"}
                                        actualPrice={actualPriceValue}
                                        propertyId={property?.id}
                                        score={score}
                                        shortLink={shortLink}
                                        propertyShareLabel={propertyShareLabel}
                                        propertyPlayDate={propertyPlayDate}
                                        registeredStreak={
                                            isAuthenticated ? streakData?.currentStreak ?? null : null
                                        }
                                        scoreBadgeGuesses={scoreBadgeGuesses}
                                        guestGuessesForScoreboard={guestGuessesForScoreboardApi}
                                        viewLocation={scoreboardViewLocation}
                                        isStreakModalOpen={showStreakModal}
                                        guestScoreboardAuthNotBeforeMs={guestScoreboardAuthNotBeforeMs}
                                        onGuestScoreboardAuthDelayConsumed={clearGuestScoreboardAuthDelay}
                                        onSaveGuestScore={handleGuestSaveFromScoreboard}
                                        puzzleVersion={version}
                                        onBeforeNavigateToNextPuzzle={() => {
                                            flushSync(() => {
                                                suppressGuessHydrationRef.current = true;
                                                resetPuzzlePlaySession();
                                                setActiveTab("guesses");
                                                setExpand(true);
                                                setScoreboardViewLocation("Puzzle Drawer");
                                            });
                                            setPuzzleView("listing", { replace: true });
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Right Sidebar Ad - Desktop Only */}
                        {!isMobile && (
                            <div className="tw-flex-shrink-0 tw-w-[320px] tw-min-w-[320px] tw-max-w-[385px] tw-pr-4">
                                <div className="tw-sticky tw-top-[calc(9svh+2rem)] tw-w-full tw-z-10">
                                    <AdSenseAd
                                        slot={config.ADSENSE_PUZZLE_SIDEBAR_SLOT_ID}
                                        format="vertical"
                                        placementType="sticky-sidebar"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}
        </>
    );
}
